import { isJointPolicy } from '../funding/calcFundingSourceDerivatives';
import type { ComparisonScenario } from './types';

/**
 * True if the derived primary has an active joint policy AND a partner
 * was supplied. A joint quote without a partner silently falls back to
 * single mode.
 */
export function isCoupleScenario(derivedPrimary: Client, derivedPartner: Client | null): boolean {
  if (!derivedPartner) return false;

  const policies = derivedPrimary.multipleFundingSources?.ltcPolicy ?? {};

  for (const policy of Object.values(policies)) {
    if (policy.isDeactivated) continue;
    if (isJointPolicy(policy.policyType, derivedPartner)) return true;
  }

  return false;
}

/**
 * Sum a numeric field across the partner's active, non-joint policies.
 *
 * Joint policy aggregates on primary already include both spouses'
 * contributions (engine sums them in the joint coverage path), so partner-
 * side joint coverage is excluded here. Partner-only standalone policies
 * (e.g. a single-life policy partner held before the joint quote) live
 * only on the partner side and must be added back when building couple
 * totals from primary aggregates.
 */
export function sumPartnerNonJointPolicies(
  partner: Client | null,
  getValue: (policy: PolicyFundingSource) => number | null | undefined,
): number {
  if (!partner) return 0;

  let total = 0;

  const policies = partner.multipleFundingSources?.ltcPolicy ?? {};

  for (const policy of Object.values(policies)) {
    if (policy.isDeactivated) continue;
    if (isJointPolicy(policy.policyType, partner)) continue;
    total += getValue(policy) ?? 0;
  }

  return total;
}

function sumActiveSelfFundingAssets(client: Client | null): number {
  if (!client) return 0;

  let total = 0;

  const sources = client.multipleFundingSources?.selfFunding ?? {};

  for (const source of Object.values(sources)) {
    if (!source.isDeactivated) total += source.existingAssetsValue ?? 0;
  }

  return total;
}

function sumActiveAnnuityCoverage(client: Client | null): number {
  if (!client) return 0;

  let total = 0;

  const sources = client.multipleFundingSources?.annuity ?? {};

  for (const annuity of Object.values(sources)) {
    if (annuity.isDeactivated) continue;
    total += annuity.annuityProjectedCostCoverage ?? 0;
  }

  return total;
}

type AggregatedMetrics = Omit<
  ComparisonScenario,
  'isCoupleMode' | 'partnerFirstName' | '_derivedPrimary' | '_derivedPartner'
>;

/**
 * Build the scenario-level numeric aggregates from a pair of derived
 * clients. All sums are couple-aware: when `isCoupleMode` is true the
 * partner contributes (joint coverage from primary, non-joint coverage
 * added separately from partner side, self-funding and annuity summed
 * across both); otherwise the partner is ignored entirely.
 *
 * Reads only from `multipleFundingSources.*` and individual funding
 * source objects — never from the singular `fundingSources` working slot,
 * whose contents reflect the last engine iteration, not aggregates.
 */
export function aggregateMetrics(
  derivedPrimary: Client,
  derivedPartner: Client | null,
  isCoupleMode: boolean,
): AggregatedMetrics {
  const partner = isCoupleMode ? derivedPartner : null;

  const primaryLtcCost = derivedPrimary.allPhaseCosts?.allPhaseInflatedProfessionalShareCost ?? 0;
  const partnerLtcCost = partner?.allPhaseCosts?.allPhaseInflatedProfessionalShareCost ?? 0;
  const projectedLtcCost = primaryLtcCost + partnerLtcCost;

  const selfFundingCoverage =
    (derivedPrimary.multipleFundingSources?.multipleSelfFundingProjectedCostCoverage ?? 0) +
    (partner?.multipleFundingSources?.multipleSelfFundingProjectedCostCoverage ?? 0);

  // Joint policy coverage already lives on primary's aggregate (engine sums
  // primary + partner contributions onto primary in the joint path). Add
  // back partner's non-joint policies, which only live on partner side.
  const partnerNonJointPolicyCoverage = sumPartnerNonJointPolicies(
    partner,
    p => p.projectedCostCoverage,
  );
  const policyCoverage =
    (derivedPrimary.multipleFundingSources?.multiplePolicyProjectedCostCoverage ?? 0) +
    partnerNonJointPolicyCoverage;

  const annuityCoverage =
    sumActiveAnnuityCoverage(derivedPrimary) + sumActiveAnnuityCoverage(partner);

  const totalCoverage = selfFundingCoverage + policyCoverage + annuityCoverage;

  const coveragePercent =
    projectedLtcCost > 0 ? Math.round((totalCoverage / projectedLtcCost) * 100) : 0;
  const coverageGap = Math.max(0, projectedLtcCost - totalCoverage);
  const surplus = Math.max(0, totalCoverage - projectedLtcCost);

  // Current assets: existingAssetsValue across active self-funding (both
  // spouses) + active policy face values (joint face is on primary; add
  // partner's non-joint face values separately).
  let currentPolicyBenefit = 0;

  for (const policy of Object.values(derivedPrimary.multipleFundingSources?.ltcPolicy ?? {})) {
    if (!policy.isDeactivated) currentPolicyBenefit += policy.policyMaximumBenefitAmount ?? 0;
  }

  currentPolicyBenefit += sumPartnerNonJointPolicies(partner, p => p.policyMaximumBenefitAmount);
  const currentAssets =
    sumActiveSelfFundingAssets(derivedPrimary) +
    sumActiveSelfFundingAssets(partner) +
    currentPolicyBenefit;

  // Projected at start of LTC: self-funding total values + policy total
  // values. Annuities are excluded — the comparison is scoped to self-
  // funding vs self-funding+insurance.
  const selfFundingTotalValue =
    (derivedPrimary.multipleFundingSources?.multipleSelfFundingTotalValue ?? 0) +
    (partner?.multipleFundingSources?.multipleSelfFundingTotalValue ?? 0);
  const partnerNonJointPolicyTotalValue = sumPartnerNonJointPolicies(
    partner,
    p => p.policyTotalValue,
  );
  const policyTotalValue =
    (derivedPrimary.multipleFundingSources?.multiplePolicyTotalValue ?? 0) +
    partnerNonJointPolicyTotalValue;
  const projectedAssetsAtStart = selfFundingTotalValue + policyTotalValue;

  // Projected at end of LTC: policy pays first, self-funding covers the
  // remainder. Hybrid/life policies convert unused benefit to a death
  // benefit payout (`multiplePolicyNonLtcPayout`); traditional LTC
  // forfeits unused benefit (that field stays at 0).
  const excessPolicyCoverage = Math.max(0, policyCoverage - projectedLtcCost);
  const policyRemainder =
    (derivedPrimary.multipleFundingSources?.multiplePolicyNonLtcPayout ?? 0) + excessPolicyCoverage;
  const costAfterPolicy = Math.max(0, projectedLtcCost - policyCoverage);
  const selfFundingSurplus = Math.max(0, selfFundingTotalValue - costAfterPolicy);
  const projectedAssetsAtEnd = selfFundingSurplus + policyRemainder;

  return {
    projectedLtcCost,
    selfFundingCoverage,
    policyCoverage,
    annuityCoverage,
    totalCoverage,
    coveragePercent,
    coverageGap,
    surplus,
    currentAssets,
    projectedAssetsAtStart,
    projectedAssetsAtEnd,
  };
}
